import { ProfileMenuOptionId, ProfileMenuOption } from "@app/models/Header";
import { profileMenuOptions } from "@app/constants/header";
import { ProfileMenuPermissions } from "@app/hooks";

/**
 * Builds the profile menu options array based on user's permissions.
 *
 * Menu Order (matches mockup): Preferences → My Profile → Manage Contacts → Impersonate User
 *
 * Menu Visibility Logic:
 *
 * | User Type                                    | My Profile | Manage Contacts | Preferences | Impersonate |
 * |----------------------------------------------|------------|-----------------|-------------|-------------|
 * | Regular investor (Tax Advisor, Wire Approver)| ✓          | ✗               | ✗           | ✗           |
 * | Primary/Secondary Contact (External funds)   | ✓          | ✓               | ✗           | ✗           |
 * | Primary Contact (Internal funds only)        | ✓          | ✓               | ✗           | ✗           |
 * | Secondary/Tax Advisor (Internal funds only)  | ✓          | ✗               | ✗           | ✗           |
 * | Admin with NO IFCs                           | ✗          | ✗               | ✓           | ✓           |
 * | Admin + IFCs + External + Primary/Secondary  | ✓          | ✓               | ✓           | ✓           |
 * | Admin + IFCs + Internal + Primary            | ✓          | ✓               | ✓           | ✓           |
 *
 * @param permissions - ProfileMenuPermissions object from useProfileMenuPermissions hook
 * @returns Array of ProfileMenuOption objects to display in the menu
 */
export const getProfileMenuOptions = (permissions: ProfileMenuPermissions): ProfileMenuOption[] => {
  const options: ProfileMenuOption[] = [];

  // Menu order matches mockup: Preferences → My Profile → Manage Contacts → Impersonate

  // 1. Preferences - Only for administrators (canImpersonate)
  if (permissions.showPreferences) {
    options.push(profileMenuOptions[ProfileMenuOptionId.Preferences]);
  }

  // 2. My Profile - Visible for users with IFCs
  if (permissions.showMyProfile) {
    options.push(profileMenuOptions[ProfileMenuOptionId.MyProfile]);
  }

  // 3. Manage Contacts - Conditional based on IFCs + roles + fund access
  if (permissions.showManageContacts) {
    options.push(profileMenuOptions[ProfileMenuOptionId.ManageContacts]);
  }

  // 4. Impersonate User - Only for administrators (canImpersonate)
  // Shows "Stop Impersonating" when actively impersonating
  if (permissions.showImpersonate) {
    options.push(
      permissions.isImpersonating
        ? profileMenuOptions[ProfileMenuOptionId.StopImpersonation]
        : profileMenuOptions[ProfileMenuOptionId.StartImpersonation]
    );
  }

  return options;
};
